import { create } from 'zustand';
import type { TimeViewOption } from '../components/ui/TimeControls';
import type { OrderHistory } from './appOrderHistoryStore';
import type { SourcingRunRequest, SourcingRunResponse } from '../services/sourcing/sourcingRunTypes';
import { canCompareSourcingUnits, chooseSourcingOptions, isSourcingSelf, sourcingHistoricalOrderDates, SourcingPeriod } from '../services/sourcing/sourcingRunCalculations';
import { fetchSourcingRun, fetchSourcingVolumeOrders } from '../services/sourcing/sourcingRunService';
import type { BackendUser } from '../services/user/userService';

export interface SourcingStoredRun {
  request: SourcingRunRequest;
  snapshot: SourcingRunResponse | null;
  status: 'loading' | 'ready' | 'error';
  error: string | null;
  period: SourcingPeriod;
  viewOption?: TimeViewOption;
  viewRestaurantIds: string[];
  selections: Record<string, string>;
  manualOptions: Record<string, string[]>;
  historicalOrderDates: Record<string, string>;
  volume: { status: 'loading' | 'ready' | 'error'; orders: OrderHistory[]; period: SourcingPeriod; error: string | null; coverage: SourcingPeriod[] };
  platformIdsByRestaurant: Record<string, string[]>;
  lastOpenedAt: number;
  generation: number;
  viewGeneration: number;
}
interface SourcingRunState {
  contextKey: string | null;
  runs: Record<string, SourcingStoredRun>;
  setAccessContext: (contextKey: string | null) => void;
  open: (entryKey: string) => void;
  start: (entryKey: string, request: SourcingRunRequest, period: SourcingPeriod, platformIdsByRestaurant: Record<string, string[]>, viewOption?: TimeViewOption) => Promise<void>;
  setView: (entryKey: string, period: SourcingPeriod, restaurantIds: string[], viewOption?: TimeViewOption) => Promise<void>;
  selectOption: (entryKey: string, parentKey: string, productKey: string) => void;
  addManualOption: (entryKey: string, parentKey: string, productKey: string) => void;
  clear: () => void;
}

/** Stable across ordinary restaurant navigation; changes with actor, account or permissions. */
export function sourcingAccessContext(actorId: string | null | undefined, user: BackendUser | null, restaurantIds: string[]): string | null {
  if (!actorId || !user) return null;
  return JSON.stringify([actorId, user.id, user.admin_user ?? null, user.role, user.access_request ?? null,
    [...(user.permissions ?? [])].sort(), [...(user.accountSettings ?? [])].sort(), user.demoContext?.ownerId ?? null,
    [...(user.demoContext?.restaurantIds ?? [])].sort(), [...restaurantIds].sort()]);
}

let generation = 0;
let openedSequence = 0;
const activeRuns = new Map<string, Promise<void>>();
const activeVolumes = new Map<string, Promise<OrderHistory[]>>();
const message = (error: unknown) => error instanceof Error ? error.message : 'Sourcing request failed';

function evict(runs: Record<string, SourcingStoredRun>): Record<string, SourcingStoredRun> {
  const completed = Object.entries(runs).filter(([, run]) => run.status !== 'loading' && run.volume.status !== 'loading').sort((a, b) => b[1].lastOpenedAt - a[1].lastOpenedAt);
  const remove = new Set(completed.slice(10).map(([key]) => key));
  return Object.fromEntries(Object.entries(runs).filter(([key]) => !remove.has(key)));
}

/** Return only uncovered inclusive windows; coverage never comes from a partial read. */
function missingPeriods(period: SourcingPeriod, coverage: SourcingPeriod[]): SourcingPeriod[] {
  let cursor = new Date(period.start).getTime();
  const end = new Date(period.end).getTime();
  if (!Number.isFinite(cursor) || !Number.isFinite(end) || cursor > end) throw new Error('Invalid sourcing purchase period');
  const missing: SourcingPeriod[] = [];
  for (const range of [...coverage].sort((a, b) => a.start.localeCompare(b.start))) {
    const start = new Date(range.start).getTime(), until = new Date(range.end).getTime();
    if (until < cursor || start > end) continue;
    if (start > cursor) missing.push({ start: new Date(cursor).toISOString(), end: new Date(start - 1).toISOString() });
    cursor = Math.max(cursor, until + 1);
  }
  if (cursor <= end) missing.push({ start: new Date(cursor).toISOString(), end: new Date(end).toISOString() });
  return missing;
}

export const useSourcingRunStore = create<SourcingRunState>((set, get) => ({
  contextKey: null,
  runs: {},
  clear: () => { generation++; activeRuns.clear(); activeVolumes.clear(); set({ contextKey: null, runs: {} }); },
  setAccessContext: contextKey => {
    if (get().contextKey !== contextKey) { get().clear(); set({ contextKey }); }
  },
  open: entryKey => {
    const run = get().runs[entryKey];
    if (run) set({ runs: { ...get().runs, [entryKey]: { ...run, lastOpenedAt: ++openedSequence } } });
  },
  start: (entryKey, request, period, platformIdsByRestaurant, viewOption) => {
    const context = get().contextKey;
    if (!context) return Promise.reject(new Error('Sourcing requires an active access context'));
    const activeKey = JSON.stringify([context, entryKey]);
    const running = activeRuns.get(activeKey);
    if (running) return running;
    if (!request.products.length || !request.targetRestaurantIds.length || request.targetRestaurantIds.some(id => !request.sourceFromRestaurantIds.includes(id) || !Object.prototype.hasOwnProperty.call(platformIdsByRestaurant, id))) {
      return Promise.reject(new Error('Invalid sourcing scope'));
    }
    const previous = get().runs[entryKey];
    const ticket = ++generation;
    const capturedRequest = JSON.parse(JSON.stringify(request)) as SourcingRunRequest;
    const platforms = Object.fromEntries(request.targetRestaurantIds.map(id => [id, [...platformIdsByRestaurant[id]]]));
    const loading: SourcingStoredRun = {
      request: capturedRequest, snapshot: previous?.snapshot ?? null, status: 'loading', error: null,
      period: { ...period }, viewOption, viewRestaurantIds: [...request.targetRestaurantIds],
      selections: previous?.selections ?? {}, manualOptions: previous?.manualOptions ?? {}, historicalOrderDates: previous?.historicalOrderDates ?? {},
      volume: { status: 'loading', orders: [], period: { ...period }, error: null, coverage: [] },
      platformIdsByRestaurant: platforms, lastOpenedAt: ++openedSequence, generation: ticket, viewGeneration: 0,
    };
    set({ runs: { ...get().runs, [entryKey]: loading } });
    const current = () => get().contextKey === context && get().runs[entryKey]?.generation === ticket;
    let operation!: Promise<void>;
    operation = (async () => {
      await Promise.resolve();
      try {
        if (!current()) return;
        const orders = await fetchSourcingVolumeOrders(platforms, period);
        if (!current()) return;
        const snapshot = await fetchSourcingRun(capturedRequest);
        if (!current()) return;
        set({ runs: evict({ ...get().runs, [entryKey]: {
          ...loading, lastOpenedAt: get().runs[entryKey].lastOpenedAt, snapshot, status: 'ready', selections: chooseSourcingOptions(snapshot), manualOptions: {}, historicalOrderDates: sourcingHistoricalOrderDates(orders, snapshot.originals),
          volume: { status: 'ready', orders, period: { ...period }, error: null, coverage: [{ ...period }] },
        } }) });
      } catch (error) {
        if (!current()) return;
        console.error('Failed to run sourcing', error);
        set({ runs: evict({ ...get().runs, [entryKey]: previous?.snapshot
          ? { ...previous, generation: ticket, status: 'ready', error: message(error),
              // The previous view request belongs to the superseded generation and cannot finish.
              volume: previous.volume.status === 'loading' ? { ...previous.volume, status: 'error', error: message(error) } : previous.volume }
          : { ...loading, status: 'error', error: message(error), volume: { ...loading.volume, status: 'error', error: message(error) } } }) });
      } finally { if (activeRuns.get(activeKey) === operation) activeRuns.delete(activeKey); }
    })();
    activeRuns.set(activeKey, operation);
    return operation;
  },
  setView: async (entryKey, period, restaurantIds, viewOption) => {
    const run = get().runs[entryKey];
    if (!run?.snapshot || run.status === 'loading' || restaurantIds.some(id => !run.request.targetRestaurantIds.includes(id))) return;
    const context = get().contextKey;
    const ticket = run.viewGeneration + 1;
    const current = () => get().contextKey === context && get().runs[entryKey]?.generation === run.generation && get().runs[entryKey]?.viewGeneration === ticket;
    const update = (next: Partial<SourcingStoredRun>) => set({ runs: evict({ ...get().runs, [entryKey]: { ...get().runs[entryKey], ...next } }) });
    update({ period: { ...period }, viewOption: viewOption ?? run.viewOption, viewRestaurantIds: [...restaurantIds], viewGeneration: ticket, volume: { ...run.volume, period: { ...period }, status: 'loading', error: null } });
    try {
      const gaps = missingPeriods(period, run.volume.coverage);
      const orders = new Map(run.volume.orders.map(order => [order.id, order]));
      for (const gap of gaps) {
        const loadKey = JSON.stringify([context, entryKey, run.generation, gap]);
        let pending = activeVolumes.get(loadKey);
        if (!pending) {
          pending = fetchSourcingVolumeOrders(run.platformIdsByRestaurant, gap);
          activeVolumes.set(loadKey, pending);
        }
        try { for (const order of await pending) orders.set(order.id, order); }
        finally { if (activeVolumes.get(loadKey) === pending) activeVolumes.delete(loadKey); }
        if (!current()) return;
      }
      if (current()) update({ volume: { status: 'ready', orders: Array.from(orders.values()), period: { ...period }, error: null, coverage: [...run.volume.coverage, ...gaps] } });
    } catch (error) {
      if (current()) {
        console.error('Failed to load sourcing purchase quantities', error);
        update({ volume: { ...run.volume, period: { ...period }, status: 'error', error: message(error) } });
      }
    }
  },
  selectOption: (entryKey, parentKey, productKey) => {
    const run = get().runs[entryKey];
    const original = run?.snapshot?.originals[parentKey];
    const candidate = productKey === parentKey ? original : run?.snapshot?.catalog[productKey];
    const available = productKey === parentKey || run?.snapshot?.options[parentKey]?.some(option => option.productKey === productKey) || run?.manualOptions[parentKey]?.includes(productKey);
    if (!run || run.status === 'loading' || !original || !candidate || !available) return;
    if (productKey !== parentKey && (isSourcingSelf(original, candidate) || !canCompareSourcingUnits(original.unit, candidate.unit) || candidate.unitPrice === null || !Number.isFinite(candidate.unitPrice) || candidate.unitPrice <= 0)) return;
    set({ runs: { ...get().runs, [entryKey]: { ...run, selections: { ...run.selections, [parentKey]: productKey } } } });
  },
  addManualOption: (entryKey, parentKey, productKey) => {
    const run = get().runs[entryKey];
    const original = run?.snapshot?.originals[parentKey], candidate = run?.snapshot?.catalog[productKey];
    if (!run || run.status === 'loading' || !original || !candidate || isSourcingSelf(original, candidate)) return;
    set({ runs: { ...get().runs, [entryKey]: { ...run, manualOptions: { ...run.manualOptions, [parentKey]: Array.from(new Set([...(run.manualOptions[parentKey] ?? []), productKey])) } } } });
  },
}));
